import { useState } from 'react'

import Badge from '@components/atoms/Badge'
import { MultiSelectField } from '@components/molecules/MultiSelectField'
import cn from '@components/utils/cn'
import { EventRsvpAction } from '@frontend/graphql/types.generated'
import { currentLocale, scopedTranslation } from '@frontend/utils/I18n'
import timeFormatter from '@utils/timeFormatter'

const t = scopedTranslation('components.event_rsvp_picker')

export type Shift = {
  id: string
  roleName: string
  startTime: string
  endTime: string
  isFull: boolean
  isAssignedToSupporter: boolean
}

type Event = {
  id: string
  name: string
  startTime: string
  ianaTimeZone: string | null
  isFull: boolean
  isPast: boolean
  hasRsvpFromSupporter: boolean
  liveShifts: Shift[]
}

type Timeframe = 'upcoming' | 'past'

export type EventRsvp = {
  eventId: string
  action: EventRsvpAction
  shiftIds?: string[]
}

type EventRsvpPickerProps = {
  events: Event[]
  value: EventRsvp[]
  onChange: (value: EventRsvp[]) => void
  disabled?: boolean
}

// Shift option values are namespaced so they never collide with an event id.
const shiftValue = (eventId: string, shiftId: string) => `shift:${eventId}:${shiftId}`
const TIMEFRAMES: Timeframe[] = ['upcoming', 'past']
const sameIds = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id) => right.includes(id))

export function EventRsvpPicker({ events, value, onChange, disabled }: EventRsvpPickerProps) {
  const [timeframe, setTimeframe] = useState<Timeframe>('upcoming')
  const changes = new Map(value.map((change) => [change.eventId, change]))
  const baselineShiftIds = (event: Event) =>
    event.liveShifts.filter((shift) => shift.isAssignedToSupporter).map((shift) => shift.id)
  const isSelected = (event: Event) =>
    changes.get(event.id)?.action !== EventRsvpAction.Cancel && (changes.has(event.id) || event.hasRsvpFromSupporter)

  const selectedShiftIds = (event: Event) => changes.get(event.id)?.shiftIds ?? baselineShiftIds(event)

  const selectedValues = events
    .filter(isSelected)
    .flatMap((event) => [event.id, ...selectedShiftIds(event).map((id) => shiftValue(event.id, id))])

  const upcomingEvents = events.filter((event) => !event.isPast)
  const pastEvents = events.filter((event) => event.isPast).reverse()
  const visibleEvents = timeframe === 'past' ? pastEvents : upcomingEvents
  const unchanged = value.length === 0
  const summarisedEvents = (unchanged ? upcomingEvents : events).filter(isSelected)
  const summarisedUpcomingCount = summarisedEvents.filter((event) => !event.isPast).length
  const summarisedPastCount = summarisedEvents.length - summarisedUpcomingCount
  const summarisedShiftCount = summarisedEvents.reduce((count, event) => count + selectedShiftIds(event).length, 0)

  const selectionLabel = () => {
    const parts = [
      summarisedUpcomingCount > 0 && t('upcoming_count', { count: summarisedUpcomingCount }),
      summarisedPastCount > 0 && t('past_count', { count: summarisedPastCount }),
      summarisedShiftCount > 0 && t('shift_count', { count: summarisedShiftCount }),
    ].filter((part): part is string => !!part)
    const summary = new Intl.ListFormat(currentLocale(), { style: 'long', type: 'conjunction' }).format(parts)

    return unchanged ? t('attending', { summary }) : t('selected', { summary })
  }

  const options = [...upcomingEvents, ...pastEvents].map((event) => {
    const full = event.isFull && !event.isPast && !event.hasRsvpFromSupporter
    const shiftCount = event.liveShifts.length
    const badgeLabel = full ? t('full') : shiftCount > 0 ? t('shift_count', { count: shiftCount }) : undefined

    return {
      value: event.id,
      label: event.name,
      description: timeFormatter.fromISO(event.startTime, event.ianaTimeZone).toShortDate(),
      badge: badgeLabel && <Badge color="muted">{badgeLabel}</Badge>,
      disabled: full,
      hidden: event.isPast !== (timeframe === 'past'),
      children: event.liveShifts.map((shift) => {
        const fullShift = shift.isFull && !shift.isAssignedToSupporter

        return {
          value: shiftValue(event.id, shift.id),
          label: shift.roleName,
          description: `${timeFormatter.fromISO(shift.startTime, event.ianaTimeZone).toShortDate()} · ${timeFormatter.timeRange(shift.startTime, shift.endTime, event.ianaTimeZone)}`,
          badge: fullShift && <Badge color="muted">{t('full')}</Badge>,
          disabled: fullShift,
        }
      }),
    }
  })

  return (
    <MultiSelectField
      options={options}
      value={selectedValues}
      onValueChange={(ids) => {
        const selected = new Set(ids)
        onChange(
          events.flatMap<EventRsvp>((event) => {
            if (!selected.has(event.id)) {
              return event.hasRsvpFromSupporter ? [{ eventId: event.id, action: EventRsvpAction.Cancel }] : []
            }

            const shiftIds = event.liveShifts
              .filter((shift) => selected.has(shiftValue(event.id, shift.id)))
              .map((shift) => shift.id)
            if (event.hasRsvpFromSupporter && sameIds(shiftIds, baselineShiftIds(event))) return []

            return [{ eventId: event.id, action: EventRsvpAction.Register, shiftIds }]
          })
        )
      }}
      disabled={disabled}
      showSelectedPills={false}
      selectedLabel={selectionLabel}
      listHeader={
        <div role="group" aria-label={t('timeframe')} className="tw:flex tw:gap-1 tw:px-3 tw:py-2">
          {TIMEFRAMES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={timeframe === option}
              onClick={() => setTimeframe(option)}
              className={cn(
                'tw:rounded-md tw:px-2 tw:py-1 tw:text-sm tw:transition-colors tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-400',
                timeframe === option
                  ? 'tw:bg-gray-100 tw:font-semibold tw:text-gray-900'
                  : 'tw:text-gray-500 tw:hover:text-gray-900'
              )}
            >
              {t(option)}
            </button>
          ))}
        </div>
      }
      label={t('title')}
      placeholder={t('placeholder')}
      searchPlaceholder={t('search_placeholder')}
      noResultsText={visibleEvents.length === 0 ? t('no_events') : t('no_search_results')}
    />
  )
}
